import React, { type FormEvent, useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { apiFactory, type ApiResult, Transfers, type TransfersResponse, TransferStatus } from '@app/client';
import config from '@app/config';
import { addNotification } from '@app/store/actions/notification';
import { cancelTransfer, getOutgoingTransfers, setTransferData } from '@app/store/actions/transfers';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import SecurityCodeInput from '../../../Input/SecurityCodeInput';
import '../YatTransferSheet.scss';

const transferCodeDigitCount = config.transferCodeDigitCount;

export function SenderCodes() {
    const [translate] = useTranslation(['transfers', 'words']);
    const { outgoingTransfers, transferData } = useAppSelector((state) => state.transfers);
    const currentEmoji = useEmojisStore((state) => state.currentEmoji);
    const [invalid, setInvalid] = useState(false);
    const [errorLabel, setErrorLabel] = useState('');
    const [code, setCode] = useState('');
    const [transferId, setTransferId] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useAppDispatch();

    function handleCode(code = '') {
        setCode(code);
    }

    useEffect(() => {
        if (!currentEmoji) return;

        const id = transferData[currentEmoji]?.id;

        if (id && typeof id !== 'undefined') setTransferId(id);
    }, [transferData, currentEmoji]);

    const submitOTP = useCallback(
        (event?: FormEvent) => {
            // Prevent refresh on submit.
            event?.preventDefault();

            setInvalid(false);

            if (transferId) {
                setIsLoading(true);

                apiFactory()
                    .call(Transfers.acceptSenderOTP, {
                        id: transferId,
                        value: {
                            code,
                        },
                    })
                    .then((r) => {
                        if (r.ok) {
                            dispatch(
                                setTransferData(currentEmoji, {
                                    status: TransferStatus.Pending,
                                    id: transferData[currentEmoji].id,
                                }),
                            );
                            dispatch(getOutgoingTransfers());
                        } else {
                            setIsLoading(false);
                            setInvalid(true);
                            setErrorLabel(translate('sender_codes_invalid_code'));
                        }
                    })
                    .catch((error) => {
                        setIsLoading(false);
                        if (error) {
                            setInvalid(true);
                            setErrorLabel(translate('sender_codes_invalid_code'));
                        }
                    });
            }
        },
        [code, transferId, currentEmoji, dispatch, translate, transferData],
    );

    const resendCode = useCallback(() => {
        if (transferId) {
            apiFactory()
                .call(Transfers.resendOTP, { value: {}, id: transferId })
                .then((r) => {
                    if (r.ok) {
                        dispatch(
                            addNotification({
                                message: translate('resend_code_alert_message'),
                                severity: 'alert',
                                title: translate('resend_code_alert_title'),
                            }),
                        );
                    } else {
                        setInvalid(true);
                        // TODO, check for actual status?
                        if (r.error && r.error?.detail === 'Too many code resends requested') {
                            setErrorLabel(r.error.toString());
                            dispatch(
                                addNotification({
                                    message: r.error.toString(),
                                    severity: 'alert',
                                    title: translate('resend_code_alert_title'),
                                }),
                            );
                        }
                    }
                })
                .catch((e) => {
                    if (e) {
                        setInvalid(true);
                    }
                });
        }
    }, [transferId, translate, dispatch]);

    const cancel = useCallback(() => {
        if (transferId) {
            const dispatchedPromise = dispatch(cancelTransfer(transferId) as unknown) as Promise<
                ApiResult<TransfersResponse>
            >;
            dispatchedPromise
                .then((r) => {
                    if (r.ok) {
                        dispatch(
                            addNotification({
                                duration: 60000,
                                message: translate('cancel_transfer_alert_message'),
                                severity: 'alert',
                                title: translate('cancel_transfer_alert_title'),
                            }),
                        );
                        dispatch(setTransferData(currentEmoji, { status: TransferStatus.Deleted }));
                    } else {
                        dispatch(addNotification({ message: 'Error', severity: 'error' }));
                    }
                })
                .catch((e) => {
                    if (e) {
                        dispatch(addNotification({ message: e.message, severity: 'error' }));
                    }
                });
        }
    }, [transferId, currentEmoji, translate, dispatch]);

    const handleKeyDown = (e, action) => {
        if (e.key === 'Enter' || e.key === ' ') {
            action();
        }
    };

    return (
        <form
            key={currentEmoji + outgoingTransfers.length.toString()}
            className="yat-transfer-sidebar__step"
            onSubmit={(e) => submitOTP(e)}
        >
            <div className="yat-transfer-sidebar__flex-item">
                <h1 className="yat-transfer-sidebar__title">{translate('sender_codes_title')}</h1>
                <p className="yat-transfer-sidebar__copy v2-text-fluid">{translate('sender_codes_body_copy')}</p>
            </div>

            <div className="yat-transfer-sidebar__input">
                <SecurityCodeInput
                    errorLabel={errorLabel}
                    setCompleteCode={handleCode}
                    isInvalid={invalid}
                    digitCount={transferCodeDigitCount}
                    digitOnly
                    onAutoSubmit={submitOTP}
                />
            </div>

            <div className="yat-transfer-sidebar__step-ctas yat-transfer-sidebar__flex-item">
                <div className="yat-transfer-sidebar__flex-item">
                    <DepButton
                        disabled={code.length < transferCodeDigitCount}
                        variant="primary"
                        display={DepButtonDisplay.minWidth}
                        size={'large'}
                        type="submit"
                        loading={isLoading}
                    >
                        {translate('sender_codes_cta_copy')}
                    </DepButton>

                    <p
                        className="yat-transfer-sidebar__link"
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => handleKeyDown(e, resendCode)}
                        onClick={() => {
                            resendCode();
                        }}
                    >
                        {translate('sender_codes_link_copy')}
                    </p>
                </div>

                <p
                    className="yat-transfer-sidebar__link yat-transfer-sidebar__link--bottom"
                    tabIndex={0}
                    role="button"
                    onClick={() => cancel()}
                    onKeyDown={(e) => handleKeyDown(e, cancel)}
                >
                    {translate('cancel_transfer_title')}
                </p>
            </div>
        </form>
    );
}
